import { useEffect, useState } from 'react';
import { supabase } from './supabase';

const VAPID_PUBLIC_KEY = 'BIb19xVrewVTqCsb40Ii8y3jcBilHhsI2DMoFnsW7nx1ul7p8r68dKBlZBJA9FebUZDDQBrdIiSHNEydfNu0V4k';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

export default function PushNotifications({ user, darkMode }) {
  // idle | requesting | granted | denied | dismissed | unsupported
  // "denied" = le NAVIGATEUR a explicitement refusé la permission (JS ne peut
  // plus jamais la redemander, seul un changement manuel dans les réglages du
  // site peut le débloquer). "dismissed" = l'utilisateur a juste cliqué
  // "Plus tard" — rien n'a été refusé, "Activer" doit encore fonctionner.
  // Ces deux états étaient auparavant confondus sous la seule valeur "denied",
  // ce qui affichait un bouton "Activer" cliquable pour un cas où il ne
  // pouvait plus jamais aboutir, sans jamais l'expliquer.
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setStatus('unsupported');
      return;
    }
    if (Notification.permission === 'granted') setStatus('granted');
    else if (Notification.permission === 'denied') setStatus('denied');
  }, []);

  const activer = async () => {
    setStatus('requesting');
    try {
      // Enregistrer le service worker
      const reg = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;

      // Demander la permission
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { setStatus('denied'); return; }

      // Créer la subscription
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });

      // Sauvegarder en base
      await supabase.from('push_subscriptions').upsert({
        user_id: user.id,
        subscription: sub.toJSON(),
      }, { onConflict: 'user_id' });

      setStatus('granted');
    } catch (err) {
      console.error('Push error:', err);
      // La permission peut très bien avoir été accordée alors que l'échec
      // vient d'ailleurs (abonnement push, écriture en base...) — dans ce
      // cas ce n'est PAS un refus navigateur, il faut permettre de réessayer
      // plutôt qu'afficher le message "refusé, change tes réglages".
      setStatus(Notification.permission === 'denied' ? 'denied' : 'idle');
    }
  };

  if (status === 'unsupported' || status === 'granted' || status === 'dismissed') return null;

  const B = '#2563eb';
  const vraimentRefuse = status === 'denied';

  return (
    <div style={{
      position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
      width: 'calc(100% - 48px)', maxWidth: '480px',
      background: darkMode ? '#1c1c1e' : '#fff',
      border: `1.5px solid ${darkMode ? '#2c2c2e' : '#e8ecf8'}`,
      borderRadius: '18px',
      padding: '18px 20px',
      boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
      display: 'flex', alignItems: 'center', gap: '14px',
      zIndex: 8000,
      animation: 'slideUp 0.4s cubic-bezier(.4,0,.2,1)',
    }}>
      <style>{`@keyframes slideUp { from { opacity:0; transform:translateX(-50%) translateY(20px); } to { opacity:1; transform:translateX(-50%) translateY(0); } }`}</style>

      <div style={{ width: '42px', height: '42px', borderRadius: '12px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={B} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: '14px', fontWeight: 700, color: darkMode ? '#f9fafb' : '#111827', margin: '0 0 2px' }}>
          {vraimentRefuse ? 'Rappels bloqués par ton navigateur' : 'Activer les rappels'}
        </p>
        <p style={{ fontSize: '12px', color: '#6b7280', margin: 0, lineHeight: 1.4 }}>
          {vraimentRefuse
            ? 'Tu as refusé les notifications. Pour les activer, autorise-les manuellement dans les réglages du site (icône 🔒 ou ⓘ à côté de l\'adresse), puis recharge la page.'
            : 'Recevez une notification 2h avant chaque intervention'}
        </p>
      </div>

      <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
        <button
          onClick={() => setStatus('dismissed')}
          style={{ padding: '8px 12px', background: 'transparent', color: '#9ca3af', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '13px' }}
        >{vraimentRefuse ? 'Fermer' : 'Plus tard'}</button>
        {!vraimentRefuse && (
          <button
            onClick={activer}
            disabled={status === 'requesting'}
            style={{ padding: '8px 16px', background: B, color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 700, fontSize: '13px' }}
          >{status === 'requesting' ? '...' : 'Activer'}</button>
        )}
      </div>
    </div>
  );
}
